import { useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { potwierdzBazeWyspy } from '@/lib/wyspa-api';
import { useAuth } from '@/lib/auth';
import { useIslands } from '@/lib/islands';
import { fetchPrzegladWysp } from '@/lib/wyspy-sprzatanie';
import { ustawRozsylanieWydan } from '@/lib/wyspa-api';
import { ustawTrybWyspy, wyczyscDaneWyspy, zmienBazeWyspy } from '@/lib/wyspa-api';

/**
 * TRYB WDROŻENIA WYSPY I JEJ BAZA MASTER.
 *
 * ⚠️ DWIE DECYZJE, KTÓRE DO 2026-08-24 NIE MIAŁY W PORTALU ŻADNEJ DROGI:
 *   • tryb — czy master stoi na maszynie (zwykły), czy u nas w chmurze (Smart Sync). Od tego zależy,
 *     czy „maszyna ma inną bazę niż wyspa" jest awarią czy stanem normalnym;
 *   • baza master — `masterServer`/`masterDatabase` ustawiała WYŁĄCZNIE pierwsza rejestracja agenta.
 *     Alert prosił „potwierdź albo popraw", a poprawić nie było czym.
 *
 * Zgłoszone z testówki: do maszyny dograno bazę `pesa`, przepięto na nią SupplyPointa i portal
 * dalej pokazywał `temrex_sdi` — z danymi sprzed dziesięciu dni i bez sposobu, żeby za tym pójść.
 */
export function TrybIBazaWyspy() {
  const { groupId, island } = useIslands();
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const przeglad = useQuery({ queryKey: ['przeglad-wysp'], queryFn: fetchPrzegladWysp });

  const [serwer, setSerwer] = useState('');
  const [baza, setBaza] = useState('');
  const [potwierdzenie, setPotwierdzenie] = useState('');
  const [wyczysc, setWyczysc] = useState(false);
  const [pracuje, setPracuje] = useState(false);
  const [pytamOTryb, setPytamOTryb] = useState(false);
  /**
   * ⚠️⚠️ DWA KLIKNIĘCIA ZAMIAST PRZEPISYWANIA KODU WYSPY. Zgłoszenie (01.09.2026) brzmiało „po
   * prostu przycisk czyszczenia danych bez wpisywania wszystkiego od nowa" — a jednocześnie to
   * czynność, która kasuje cały read-model, więc jedno przypadkowe kliknięcie nie może jej odpalić.
   * Uzbrojony przycisk sam się rozbraja po chwili: uzbrojenie sprzed pięciu minut, o którym nikt
   * nie pamięta, byłoby gorsze niż brak bramki.
   */
  const [wipeUzbrojony, setWipeUzbrojony] = useState(false);
  const [pytamORodzenstwo, setPytamORodzenstwo] = useState(false);
  /** Decyzja o HR+ przy czyszczeniu — domyślnie „wyłącz", bo wyspa w trakcie odbudowy jest dla HR+ w połowie pusta. */
  const [wylaczHr, setWylaczHr] = useState(true);

  if (groupId === null || user?.role !== 'ADMIN') return null;
  const wyspa = przeglad.data?.wyspy.find((w) => w.id === groupId);
  if (przeglad.isPending) {
    return (
      <section className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
        Wczytuję ustawienia wdrożenia wyspy…
      </section>
    );
  }
  if (wyspa === undefined) return null;

  const smart = wyspa.tryb === 'SMART_SYNC';
  /** Wyspa z działającą integracją HR+ — serwer nie wyczyści jej bez decyzji (`wylaczHrPlus`). */
  const hrPlus = island?.hrPlus === true;
  const decyzjaHr = hrPlus ? { wylaczHrPlus: wylaczHr } : {};

  const przelaczTryb = async (): Promise<void> => {
    setPracuje(true);
    try {
      const w = await ustawTrybWyspy(groupId, smart ? 'ZWYKLY' : 'SMART_SYNC');
      toast.success(
        w.tryb === 'SMART_SYNC'
          ? 'Wyspa pracuje w trybie Smart Sync — master jest w chmurze, maszyny są slave’ami'
          : 'Wyspa wróciła do trybu zwykłego — master stoi na maszynie',
      );
      void qc.invalidateQueries({ queryKey: ['przeglad-wysp'] });
      /** Plakietka w nagłówku czyta listę wysp, nie przegląd — bez tego świeciłaby starym trybem. */
      void qc.invalidateQueries({ queryKey: ['islands'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać trybu');
    } finally {
      setPracuje(false);
    }
  };

  const wipeDanych = async (): Promise<void> => {
    setPracuje(true);
    try {
      const w = await wyczyscDaneWyspy(groupId, decyzjaHr);
      toast.success(
        `Wyczyszczono dane wyspy „${w.code}" (${w.wierszy} wierszy). Agenci przyślą konfigurację i wydania ` +
          `od początku w ciągu kilku cykli` +
          (w.zmianWKolejce > 0
            ? `; anulowano ${w.zmianWKolejce} ${w.zmianWKolejce === 1 ? 'operację' : 'operacji'} z kolejki`
            : '') +
          (w.hrPlusWylaczona === true ? '; integracja HR+ wyłączona' : ''),
      );
      setWipeUzbrojony(false);
      /** ⚠️ Wszystko na ekranie mówi teraz o pustej wyspie — odświeżamy CAŁOŚĆ, nie tylko przegląd. */
      void qc.invalidateQueries();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się wyczyścić danych wyspy');
    } finally {
      setPracuje(false);
    }
  };

  const zapiszBaze = async (): Promise<void> => {
    setPracuje(true);
    try {
      const w = await zmienBazeWyspy(groupId, {
        server: serwer.trim() === '' ? null : serwer.trim(),
        database: baza.trim(),
        potwierdzenie,
        wyczyscDane: wyczysc,
        ...(wyczysc ? decyzjaHr : {}),
      });
      const ile = w.usunieteWiersze.reduce((s, x) => s + x.usunieto, 0);
      toast.success(
        `Baza wyspy to teraz „${w.masterDatabase}"` +
          (w.wyczyszczono
            ? `; wyczyszczono read-model (${ile} wierszy) i poproszono agentów o wydania od początku`
            : '') +
          (w.hrPlusWylaczona === true ? '; integracja HR+ wyłączona' : ''),
      );
      setBaza('');
      setSerwer('');
      setPotwierdzenie('');
      setWyczysc(false);
      void qc.invalidateQueries();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zmienić bazy');
    } finally {
      setPracuje(false);
    }
  };

  const rodzenstwoWlaczone = wyspa.wydaniaRodzenstwa === true;

  const przelaczRodzenstwo = async (): Promise<void> => {
    setPracuje(true);
    try {
      const w = await ustawRozsylanieWydan(groupId, !rodzenstwoWlaczone);
      toast.success(
        w.wlaczone
          ? `Rozsyłanie wydań rodzeństwa WŁĄCZONE — maszyny zaczną dostawać cudze wydania od numeru ${w.kursorOd ?? '0'}`
          : 'Rozsyłanie wydań rodzeństwa WYŁĄCZONE — maszyny przestają dostawać cudze wydania',
      );
      void qc.invalidateQueries({ queryKey: ['przeglad-wysp'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać ustawienia');
    } finally {
      setPracuje(false);
      setPytamORodzenstwo(false);
    }
  };

  /**
   * POTWIERDZENIE BAZY, KTORA WYSPA JUZ MA.
   *
   * ⚠️ BEZ PYTANIA W OKNIE, i to jest swiadome: ta czynnosc nic nie zmienia w danych ani w bazie
   * klienta — zapisuje odpowiedz „tak, ta nazwa jest wlasciwa" na pytanie, ktore zadal alert.
   * Odwrotnosc (zmiana bazy) stoi obok i wymaga przepisania kodu wyspy, bo tam kasuje sie read-model.
   */
  const potwierdz = async (): Promise<void> => {
    setPracuje(true);
    try {
      const w = await potwierdzBazeWyspy(groupId);
      toast.success(`Baza master „${w.baza}" potwierdzona — alert o zatwierdzeniu zgaśnie przy najbliższym przebiegu`);
      void qc.invalidateQueries({ queryKey: ['przeglad-wysp'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się potwierdzić bazy');
    } finally {
      setPracuje(false);
    }
  };

  const gotowe = baza.trim() !== '' && potwierdzenie.trim() === wyspa.code;

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      {/*
        ⚠️ BEZ AKAPITU POD TYTULEM — zgloszone wprost (28.08.2026). Mowil, ze ekran widzi tylko konto
        SDI; kto go widzi, ten juz to wie, a kto nie ma prawa, ten go nie zobaczy (`SdiOnlyGuard`).
      */}
      <h2 className="font-semibold">Model wdrożenia i baza wyspy</h2>

      <div className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm">
        <span className="text-xs text-muted-foreground">Tryb wyspy:</span>
        <Badge variant={smart ? 'default' : 'neutral'}>{smart ? 'Smart Sync' : 'zwykły'}</Badge>
        {/*
          ⚠️ PYTAMY, BO TEN PRZYCISK BYŁ JEDNYM KLIKNIĘCIEM BEZ ODWROTU W ŚRODKU ZDANIA.
          Zgłoszone przez użytkownika 2026-08-24: „za łatwo się klika". Zmiana trybu rozstrzyga,
          czy maszyna z inną bazą niż master jest usterką, czy stanem normalnym — czyli włącza albo
          wyłącza synchronizację całym maszynom. Nie kasuje danych, więc nie żądamy przepisywania
          kodu wyspy jak przy zmianie bazy; wystarczy, żeby człowiek zobaczył SKUTEK przed kliknięciem.
        */}
        <Dialog open={pytamOTryb} onOpenChange={setPytamOTryb}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="h-7" disabled={pracuje}>
              {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {smart ? 'Przełącz na zwykły' : 'Przełącz na Smart Sync'}
            </Button>
          </DialogTrigger>
          <DialogContent title={smart ? 'Przełączyć na tryb zwykły?' : 'Przełączyć na Smart Sync?'}>
            <div className="space-y-3 text-sm">
              {smart ? (
                <p>
                  Master wróci <b>na maszynę</b>. Od tej chwili agent zgłaszający bazę inną niż{' '}
                  <code>{wyspa.masterDatabase ?? '(nieustalona)'}</code> będzie traktowany jako <b>usterka</b>{' '}
                  i przestanie synchronizować.
                </p>
              ) : (
                <p>
                  Master będzie <b>w naszym MSSQL-u</b>, a każda maszyna dostanie własną bazę lokalną. Różnica
                  wobec mastera przestanie być alarmem i maszyny z własną bazą <b>zaczną synchronizować</b>.
                </p>
              )}
              {/*
                ⚠️ TO JEST NAJWAŻNIEJSZE ZDANIE W TYM OKNIE. `canSyncDb` jest własnością TOKENU
                z rejestracji i nic go nie przelicza po zmianie trybu ani bazy — zmierzone na
                Lockerboksie: wyspa przeszła na Smart Sync, a agent dalej stał jako maszyna podległa.
                Bez tego ostrzeżenia operator klika, nic się nie dzieje i nie ma jak zgadnąć dlaczego.
              */}
              <p className="rounded border border-amber-500/40 bg-amber-500/10 p-2">
                ⚠️ Sama zmiana trybu <b>nie przestawia agentów, które już są zarejestrowane</b> — każdy zachowuje
                uprawnienie do synchronizacji z chwili rejestracji. Maszyny, których to ma dotyczyć, trzeba{' '}
                <b>zarejestrować ponownie</b> instalatorem.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPytamOTryb(false)}>
                Anuluj
              </Button>
              <Button
                disabled={pracuje}
                onClick={() => {
                  setPytamOTryb(false);
                  void przelaczTryb();
                }}
              >
                {smart ? 'Przełącz na zwykły' : 'Przełącz na Smart Sync'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <span className="max-w-3xl text-[11px] text-muted-foreground">
          W trybie <b>zwykłym</b> master to baza na maszynie klienta, a agent zgłaszający inną bazę to sygnał usterki.
          W <b>Smart Syncu</b> master stoi w naszym MSSQL-u, a każda maszyna ma własną bazę lokalną — różnica wobec
          mastera jest wtedy stanem normalnym i portal o niej nie alarmuje. Zgłasza natomiast, gdy maszyna bazę
          ZMIENI.
        </span>
      </div>

      {/*
        ROZSYŁANIE WYDAŃ RODZEŃSTWA — najbardziej ryzykowny mechanizm w projekcie (PLAN §35 pkt 8).
        ⚠️ POKAZUJEMY GO WYŁĄCZNIE W SMART SYNCU, bo w trybie zwykłym wyspa ma jedną bazę i nie ma
        czego rozsyłać — przełącznik, którego nie da się sensownie użyć, uczy klikać na oślep.
      */}
      {smart && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm">
          <span className="text-xs text-muted-foreground">Wydania rodzeństwa:</span>
          <Badge variant={rodzenstwoWlaczone ? 'default' : 'neutral'}>
            {rodzenstwoWlaczone ? 'rozsyłane' : 'nie rozsyłane'}
          </Badge>
          <Dialog open={pytamORodzenstwo} onOpenChange={setPytamORodzenstwo}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-7" disabled={pracuje}>
                {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {rodzenstwoWlaczone ? 'Wyłącz rozsyłanie' : 'Włącz rozsyłanie'}
              </Button>
            </DialogTrigger>
            <DialogContent
              title={rodzenstwoWlaczone ? 'Wyłączyć rozsyłanie wydań rodzeństwa?' : 'Włączyć rozsyłanie wydań rodzeństwa?'}
            >
              <div className="space-y-3 text-sm">
                {rodzenstwoWlaczone ? (
                  <p>
                    Maszyny przestaną dostawać wydania z pozostałych automatów. Te, które już u nich są,
                    <b> zostaną</b> — nie kasujemy niczego z bazy klienta. Limity wrócą do liczenia{' '}
                    <b>tylko z własnych wydań</b> każdej maszyny.
                  </p>
                ) : (
                  <>
                    <p>
                      Każda maszyna zacznie dostawać wydania <b>pozostałych automatów tej wyspy</b> i wpisywać je
                      do swojej <code>TransactionLog</code>. Po to, żeby SPS liczył limity z całej wyspy, a nie
                      z jednego automatu — on czyta wyłącznie własną tabelę.
                    </p>
                    {/*
                      ⚠️ TO ZDANIE MUSI TU BYĆ: to jedyne miejsce w portalu, które pisze do TransactionLog,
                      czyli do tabeli zakazanej dla wszystkich innych ścieżek.
                    */}
                    <p className="text-[var(--warning)]">
                      To jedyna funkcja portalu, która <b>dopisuje wiersze do dziennika wydań</b> u klienta.
                      Wpisy są idempotentne (klucz <code>VMID + TLID</code>), ale wpisane <b>zostają</b> —
                      wyłączenie zatrzymuje kolejne, nie cofa poprzednich.
                    </p>
                    <p>
                      Historia <b>nie</b> pojedzie: maszyny zaczną od <b>bieżącego numeru</b>, czyli dostaną tylko
                      to, co wydarzy się od tej chwili. Wyrównanie historii to osobna robota (scalanie baz).
                    </p>
                  </>
                )}
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setPytamORodzenstwo(false)} disabled={pracuje}>
                    Anuluj
                  </Button>
                  <Button size="sm" onClick={() => void przelaczRodzenstwo()} disabled={pracuje}>
                    {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {rodzenstwoWlaczone ? 'Wyłącz' : 'Włącz'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="space-y-2 rounded border border-border p-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Baza master wyspy:</span>
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{wyspa.masterDatabase ?? '(nie ustalona)'}</code>
          {wyspa.masterServer !== null && (
            <span className="text-[11px] text-muted-foreground">na serwerze {wyspa.masterServer}</span>
          )}
          <Badge variant={wyspa.masterConfirmed ? 'success' : 'warning'}>
            {wyspa.masterConfirmed ? 'potwierdzona' : 'niepotwierdzona'}
          </Badge>
          {/*
            ⚠️⚠️ PRZYCISK, KTOREGO DO 28.08.2026 NIE BYLO — i to byla dziura w czynnosciach, nie
            w danych. Alert `MASTER_WYSPY_DO_ZATWIERDZENIA` mowi „Potwierdz albo popraw", a
            POTWIERDZIC nie bylo czym: `masterConfirmed` ustawiala wylacznie zmiana bazy, czyli
            przepisanie tej samej nazwy ORAZ kodu wyspy. Zgloszone z instancji w trybie zwyklym:
            „ciagle mruga «niepotwierdzona», a to pojedynczy automat z baza na sobie".

            ⚠️ POKAZUJEMY WYLACZNIE, GDY JEST CO POTWIERDZAC: baza istnieje i nie jest potwierdzona.
          */}
          {!wyspa.masterConfirmed && wyspa.masterDatabase !== null && (
            <Button size="sm" variant="outline" className="h-7" disabled={pracuje} onClick={() => void potwierdz()}>
              {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Potwierdź tę bazę
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Nowa baza</span>
            {/*
              ⚠️ BEZ „np. pesa" — zgloszone: „nie moze w garosie podpowiadac «np. pesa»". Nazwa bazy
              innego klienta w polu, ktore zmienia baze TEJ wyspy, jest podpowiedzia prowadzaca
              w zle miejsce, a przy niej stoi przycisk kasujacy read-model.
            */}
            <Input
              value={baza}
              onChange={(e) => setBaza(e.target.value)}
              placeholder="nazwa bazy SPS"
              className="h-8 w-48 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Serwer (opcjonalnie)</span>
            <Input
              value={serwer}
              onChange={(e) => setSerwer(e.target.value)}
              placeholder="np. .\EXP_2019"
              className="h-8 w-48 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Wpisz kod wyspy ({wyspa.code})</span>
            <Input
              value={potwierdzenie}
              onChange={(e) => setPotwierdzenie(e.target.value)}
              placeholder={wyspa.code}
              className="h-8 w-48 text-xs"
            />
          </label>
          <Button size="sm" className="h-8" disabled={pracuje || !gotowe} onClick={() => void zapiszBaze()}>
            {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Zmień bazę
          </Button>
        </div>

        {/*
          ⚠️ CZYSZCZENIE JAKO ŚWIADOMY WYBÓR, NIE DOMYŚLNY SKUTEK. Zostawienie read-modelu przy
          zmianie bazy zlepia dwa niezależne zbiory: te same `(VMID, TLID)` znaczą wtedy różne
          wydania, a drugie odpada jako duplikat — czyli po cichu przepada. Wyczyszczenie oddaje
          czystą wyspę, którą agent odbuduje. Obie drogi są uprawnione i to człowiek wybiera.
        */}
        <label className="flex max-w-3xl items-start gap-2 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={wyczysc}
            onChange={(e) => setWyczysc(e.target.checked)}
          />
          <span>
            <b>Wyczyść dane wyspy do zera</b> — automaty, planogram, katalogi i wydania ({wyspa.liczniki.wydania}{' '}
            wierszy). Wyspa, węzły, tokeny, alerty i audyt ZOSTAJĄ. Agenci dostaną prośbę o przysłanie konfiguracji
            i wydań od początku, więc read-model odbuduje się z nowej bazy w ciągu kilku cykli. Bez tego zaznaczenia
            dane z poprzedniej bazy zostają obok nowych.
          </span>
        </label>
        {wyczysc && hrPlus && <HrPlusPrzyCzyszczeniu wylacz={wylaczHr} onZmiana={setWylaczHr} />}
      </div>

      {/*
        ══════════════════════════════════════════════════════════════════════════════════════════
         WIPE BEZ ZMIANY BAZY.

         ⚠️⚠️ ZGŁOSZONE (01.09.2026): „stawiamy nową instancję smart sync i okazało się, że miała
         starą wersję SPS i została wgrana nowsza — dodaj przycisk czyszczenia danych bez wpisywania
         wszystkiego od nowa". Dotąd czyszczenie było POLEM PRZY ZMIANIE BAZY, więc żeby wyczyścić
         dane, trzeba było przepisać nazwę bazy, serwer i kod wyspy — udawać zmianę czegoś, co się
         nie zmienia. Przy okazji odbijało się od bramki „zmiany w kolejce", która wipe'u nie dotyczy.

         ⚠️ OSOBNY BLOK, NIE KOLEJNE POLE W FORMULARZU BAZY: to inna czynność i ma inne skutki.
        ══════════════════════════════════════════════════════════════════════════════════════════
      */}
      <div className="mt-3 space-y-2 rounded border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium">Wyczyść dane wyspy (wipe)</span>
          <Button
            size="sm"
            variant={wipeUzbrojony ? 'destructive' : 'outline'}
            className="h-7"
            disabled={pracuje}
            onClick={() => {
              if (!wipeUzbrojony) {
                setWipeUzbrojony(true);
                /** ⚠️ ROZBRAJAMY PO CHWILI — uzbrojenie, o którym nikt nie pamięta, jest pułapką. */
                setTimeout(() => setWipeUzbrojony(false), 8_000);
                return;
              }
              void wipeDanych();
            }}
          >
            {pracuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {wipeUzbrojony
              ? `Na pewno? Kliknij, żeby wyczyścić (${wyspa.liczniki.wydania} wydań${hrPlus && wylaczHr ? ', HR+ zostanie wyłączone' : ''})`
              : 'Wyczyść dane'}
          </Button>
          {wipeUzbrojony && <span className="text-[11px] text-muted-foreground">rozbroi się samo po chwili</span>}
        </div>
        {hrPlus && <HrPlusPrzyCzyszczeniu wylacz={wylaczHr} onZmiana={setWylaczHr} />}
        <p className="max-w-3xl text-[11px] leading-snug text-muted-foreground">
          Kasuje automaty, planogram, katalogi, wydania i ZAPAMIĘTANY KSZTAŁT TABEL — ta ostatnia rzecz jest
          najważniejsza po podniesieniu wersji SPS, bo dopóki portal pamięta stary kształt, agent przysyła tylko
          różnice względem niego. Baza master, wyspa, węzły, tokeny, alerty, audyt i obrazki z portalu ZOSTAJĄ.
          Agenci dostaną prośbę o konfigurację i wydania od początku, więc read-model odbuduje się w ciągu kilku cykli.
          {/*
            ⚠️ MÓWIMY O KOLEJCE, bo to była realna niespodzianka: po wipie wisiały 23 operacje
            zbudowane z read-modelu, którego już nie ma (zgłoszone 02.09.2026 z Donaldsona).
          */}{' '}
          Niedokończone operacje w kolejce zostają <b>anulowane</b> — powstały ze skasowanego obrazu.
          {/*
            ⚠️ MÓWIMY, CZEGO NIE ODZYSKAMY. „Wszystko się odbuduje" byłoby nieprawdą: prosimy agenta
            o `TransactionLog` od początku, ale jeśli klient przycina tę tabelę, historia starsza od
            jego przycięcia zostaje tylko u nas — a po wipie nie zostaje nigdzie.
          */}{' '}
          <b>Nie wrócą wydania, których SPS już u siebie nie ma</b> (jeśli klient przycina
          <code className="mx-1 font-mono">TransactionLog</code>, starsza historia przepada).
        </p>
      </div>
    </section>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  OSTRZEŻENIE HR+ PRZY CZYSZCZENIU WYSPY (13.10.2026, user: „dodaj ostrzeżenie przy czyszczeniu
 *  wyspy od razu z pytaniem, czy wyłączyć tę integrację").
 *
 *  ⚠️ CO SIĘ DZIEJE Z HR+ PO WIPIE — zmierzone na DBW 13.10: osoby wracają bez powiązania z HR+
 *  (do najbliższej pełnej listy cała wyspa jest „Spoza HR+"), automaty i grupy dostają NOWE
 *  identyfikatory, a HR+ trzyma stare — dopóki ich nie pobierze od nowa, jego listy grup i automatów
 *  nie są stosowane. Wyjątki grup serwer czyści, bo wskazywałyby grupy, których już nie ma.
 *
 *  ⚠️ DOMYŚLNIE „WYŁĄCZ": przez całą odbudowę read-modelu HR+ widzi wyspę w połowie pustą.
 *  Włączenie z powrotem (ekran HR+) zaczyna od pełnej listy i wiąże osoby po numerze karty.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function HrPlusPrzyCzyszczeniu({ wylacz, onZmiana }: { wylacz: boolean; onZmiana: (v: boolean) => void }) {
  return (
    <div className="max-w-3xl space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-800 dark:text-amber-300">
      <p>
        <b>Na tej wyspie działa integracja HR+.</b> Po czyszczeniu automaty i grupy dostaną nowe identyfikatory —
        HR+ musi pobrać je od nowa, a wyjątki od blokowania trzeba ustawić ponownie.
      </p>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={wylacz} onChange={(e) => onZmiana(e.target.checked)} />
        <span>Wyłącz integrację HR+ (zalecane — włączysz ją na ekranie HR+, gdy dane wrócą)</span>
      </label>
    </div>
  );
}
